import { useSuspenseQuery } from '@tanstack/react-query'
import { useDeferredValue } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Board } from './-components/shared/board'
import { BoardPending } from './-components/shared/board-pending'
import { useReport } from '@/hooks/use-report'
import { TeamsEmptyState } from './-components/teams/teams-empty-state'
import { ReportError } from './-components/shared/report-error'
import { EmptyState } from './-components/shared/empty-state'

export const Route = createFileRoute('/_app/teams/$teamId/board')({
  component: RouteComponent,
  pendingComponent: BoardPending,
  // Shown immediately: at the router's default pendingMs the NAV STACK cannot push while the loaders run,
  // because the level it would push is the route being rendered — see the full story in jobs.$jobId.report.
  pendingMs: 0,
  pendingMinMs: 0,
})

function RouteComponent() {
  const context = Route.useRouteContext()
  const { report } = useReport(context.reportQueryOptions)
  const { teamId } = Route.useParams()
  // The layout above declares and warms this; deferring the OBJECT (stable per match) keeps the
  // previous team on screen while a filter/type change loads.
  const deferredTeam = useDeferredValue(context.teamQueryOptions)
  const { data: team } = useSuspenseQuery(deferredTeam)

  // `visibleMemberIds` is already restricted to the selected self-assessments for `ownsManual`
  // (server-side); it carries ids rather than a second copy of the member objects, so select from
  // `members`, whose order (ranked by match percentage) is the order to keep.
  const visibleIds = new Set(team?.visibleMemberIds ?? [])
  const boardProfiles = (team?.members ?? [])
    .filter((profile) => visibleIds.has(profile.id))
    .map((profile) => ({ ...profile, id: profile.id.toString() }))

  if (team?.error === 'teams-see-owns') {
    return <ReportError error="noPermissionsSeeOwns" surface="board" />
  }

  if (team?.emptyStateCode) {
    return <EmptyState code={team.emptyStateCode} surface="board" />
  }
  // Residual `profile-blocked` with members present (not a count-empty state).
  if (team?.error) {
    return <TeamsEmptyState type="no-members" teamId={teamId} />
  }

  return (
    <Board report={report} name={team?.name ?? ''} profiles={boardProfiles} standardIndicators={team?.indicators} />
  )
}
